import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import KeyboardEventHandler from 'react-keyboard-event-handler';

import { useFight } from './Fight';
import { sendAction } from '../services/actions/action-sender';

import { grapplingKeys, keys, wrestlingKeys } from '../utils/actionKeys';
import { toLegacyFightId } from '../utils/legacyId';
import {
  blueCornerDomainKeys,
  domainKeys,
  getDomainStanceKeys,
  redCornerDomainKeys,
} from '../utils/fightKeys';

const significantStrikekRed = ['e', 'w', 'q'];
const significantStrikesBlue = ['i', 'o', 'p'];

/**
 * `domain` da action: `true` quando o autor é quem detém o domínio de
 * referência, `false` quando é o dominado, `null` quando não há domínio.
 */
function domainForAction(
  referenceDomain: string | null,
  isRedCornerAction: boolean,
): boolean | null {
  if (!referenceDomain) return null;
  if (redCornerDomainKeys.includes(referenceDomain)) return isRedCornerAction;
  if (blueCornerDomainKeys.includes(referenceDomain)) return !isRedCornerAction;
  return null;
}

interface KeyContextData {
  handleCloseModalFightEnd(): void;
  fightEnd: boolean;
  emitFightEnd(type: string): void;
  /** Modal de confirmação do reset da luta. */
  resetRequested: boolean;
  openResetModal(): void;
  handleCloseModalReset(): void;
  emitReset(): Promise<void>;
  /** Há algum modal aberto? Enquanto houver, o teclado não grava ações. */
  isAnyModalOpen: boolean;
  setModalOpen(id: string, isOpen: boolean): void;
}
const KeyContext = createContext<KeyContextData>({} as KeyContextData);

const KeyProvider: React.FC = ({ children }) => {
  const [fightEnd, setFightEnd] = useState<boolean>(false);
  const [resetRequested, setResetRequested] = useState<boolean>(false);
  /** Ids dos modais abertos. Um Set porque a página tem modais próprios
   * (resultado, round) além dos dois que vivem aqui. */
  const [openModals, setOpenModals] = useState<Set<string>>(() => new Set());

  const { fight, getFight, getRoundTimeInSeconds, updateFight } = useFight();

  // const { emit, isConnected } = useSocketV1Instance();

  const domainsKeys = getDomainStanceKeys(fight.fightType);

  // Condições para wrestling
  let ActionKeys = keys;
  if (fight.fightType === 'WRESTLING') {
    ActionKeys = wrestlingKeys;
  } else if (fight.fightType === 'SUBMISSION_GRAPPLING') {
    ActionKeys = grapplingKeys;
  }

  const handleCloseModalFightEnd = useCallback(() => {
    setFightEnd(false);
  }, []);

  const openResetModal = useCallback(() => {
    setResetRequested(true);
  }, []);

  const handleCloseModalReset = useCallback(() => {
    setResetRequested(false);
  }, []);

  const setModalOpen = useCallback((id: string, isOpen: boolean) => {
    setOpenModals((prev) => {
      if (prev.has(id) === isOpen) return prev;
      const next = new Set(prev);
      if (isOpen) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const isAnyModalOpen = openModals.size > 0 || fightEnd || resetRequested;

  const emitReset = useCallback(async () => {
    await sendAction('reset', {
      promotionId: fight.promotionId,
      data: {
        fightId: toLegacyFightId(fight.legacyId),
        fightNewId: fight.id,
        round: fight.currentRound,
        roundTime: getRoundTimeInSeconds(),
        boutId: fight.oneFightId,
        eventId: fight.oneEventId || fight.eventId,
      },
    });

    setResetRequested(false);
  }, [fight, getRoundTimeInSeconds]);

  const emitFightEnd = useCallback(async (type: string) => {
    const recordData = {
      fightId: toLegacyFightId(fight.legacyId),
      fightNewId: fight.id,
      round: fight.currentRound,
      stance: 'standing',
      domain: null,
      roundTime: getRoundTimeInSeconds(),
      name: ActionKeys.x.actionName,
      corner: ActionKeys.x.isRedCorner ? 'red' : 'blue',
      boutId: fight.oneFightId,
      eventId: fight.oneEventId || fight.eventId,
      authorId: ActionKeys.x.isRedCorner
        ? fight.legacyRedCornerId
        : fight.legacyBlueCornerId,
      targetId: ActionKeys.x.isRedCorner
        ? fight.legacyBlueCornerId
        : fight.legacyRedCornerId,
      authorNewId: ActionKeys.x.isRedCorner
        ? fight.redCornerId
        : fight.blueCornerId,
      targetNewId: ActionKeys.x.isRedCorner
        ? fight.blueCornerId
        : fight.redCornerId,
      extraFields: {
        type,
      },
    };

    await sendAction('record', {
      promotionId: fight.promotionId,
      data: recordData,
    });

    setFightEnd(false);
  }, [fight, getRoundTimeInSeconds, ActionKeys]);

  const contextValue = useMemo(
    () => ({
      handleCloseModalFightEnd,
      fightEnd,
      emitFightEnd,
      resetRequested,
      openResetModal,
      handleCloseModalReset,
      emitReset,
      isAnyModalOpen,
      setModalOpen,
    }),
    [
      handleCloseModalFightEnd,
      fightEnd,
      emitFightEnd,
      resetRequested,
      openResetModal,
      handleCloseModalReset,
      emitReset,
      isAnyModalOpen,
      setModalOpen,
    ]
  );

  const handleKeyEvent = useCallback((keyEvent, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Com um modal aberto o foco é do mouse; teclar não pode gravar ação.
    if (isAnyModalOpen) return;

    // Leitura síncrona: duas teclas de domínio no mesmo tick precisam enxergar
    // o domínio já trocado pela primeira, senão o controle dela nunca fecha.
    const current = getFight();
    const activeDomain = current.currentDomain;
    const isDomainKey = domainKeys.includes(keyEvent);

    // A transição inteira é decidida aqui, antes de emitir qualquer action.
    const nextDomain = isDomainKey
      ? keyEvent === activeDomain
        ? null
        : keyEvent
      : activeDomain;
    const nextStance = isDomainKey
      ? nextDomain
        ? domainsKeys[nextDomain]
        : 'standing'
      : current.currentStance;

    let keysToEmit = [];
    keysToEmit.push(keyEvent);
    if (isDomainKey) {
      // change domain
      if (activeDomain) {
        keysToEmit.unshift(`shift+${activeDomain}`);
      }

      if (keyEvent === activeDomain) {
        keysToEmit = [];
        keysToEmit.push(`shift+${keyEvent}`);
      }
    }

    if (
      current.fightType !== 'WRESTLING' &&
      current.fightType !== 'SUBMISSION_GRAPPLING'
    ) {
      if (significantStrikekRed.includes(keyEvent)) {
        // compute significant strikes red corner
        keysToEmit.push('shift+e');
        keysToEmit.push('r');
      }

      if (significantStrikesBlue.includes(keyEvent)) {
        // compute significant strikes blue corner
        keysToEmit.push('shift+i');
        keysToEmit.push('u');
      }
    }
    keysToEmit.forEach((key) => {
      // Uma tecla derivada pode não existir na modalidade (grappling não tem
      // 4/8). Ignorar em silêncio é melhor que estourar no meio da transição.
      if (!ActionKeys[key]) return;

      const isRedCornerAction = ActionKeys[key].isRedCorner;

      // Cada tecla emitida é avaliada contra o domínio que vale PARA ELA:
      // o `controlEnded` fecha o domínio antigo, o `controlStarted` abre o
      // novo, e as demais (golpes) acontecem dentro do domínio vigente.
      const isClosingKey = !!activeDomain && key === `shift+${activeDomain}`;
      const isOpeningKey = isDomainKey && !!nextDomain && key === keyEvent;

      let referenceDomain = activeDomain;
      let stance = current.currentStance;

      if (isClosingKey) {
        stance = domainsKeys[activeDomain as string];
      } else if (isOpeningKey) {
        referenceDomain = nextDomain;
        stance = nextStance;
      }

      const domain = domainForAction(referenceDomain, isRedCornerAction);

      // A tecla só pede confirmação; o envio sai do modal. Antes o `confirm`
      // ficava dentro da condição, então cancelar caía no `else if` de baixo e
      // gravava um record com name 'reset' na luta.
      if (key === '6') {
        openResetModal();
      } else if (key === 'x') {
        setFightEnd(true);
      } else if (key !== 'space') {
        const recordData = {
          fightId: toLegacyFightId(fight.legacyId),
          fightNewId: fight.id,
          round: current.currentRound,
          stance,
          domain,
          roundTime: getRoundTimeInSeconds(),
          name: ActionKeys[key].actionName,
          corner: ActionKeys[key].isRedCorner ? 'red' : 'blue',
          boutId: fight.oneFightId,
          eventId: fight.oneEventId || fight.eventId,
          authorId: ActionKeys[key].isRedCorner
            ? fight.legacyRedCornerId
            : fight.legacyBlueCornerId,
          targetId: ActionKeys[key].isRedCorner
            ? fight.legacyBlueCornerId
            : fight.legacyRedCornerId,
          authorNewId: ActionKeys[key].isRedCorner
            ? fight.redCornerId
            : fight.blueCornerId,
          targetNewId: ActionKeys[key].isRedCorner
            ? fight.blueCornerId
            : fight.redCornerId,
        };

        void sendAction('record', {
          promotionId: fight.promotionId,
          data: recordData,
        });
      }
    });

    // Um único ponto de verdade para os dois campos. Antes o stance era
    // atualizado pelo FightKeys, num handler separado, e os dois divergiam.
    if (isDomainKey) {
      updateFight({
        currentDomain: nextDomain,
        currentStance: nextStance,
      });
    }
  }, [fight,
    getFight,
    updateFight,
    ActionKeys,
    getRoundTimeInSeconds,
    domainsKeys,
    setFightEnd,
    isAnyModalOpen,
    openResetModal]);

  return (
    <>
      <KeyboardEventHandler
        handleKeys={Object.keys(ActionKeys)}
        onKeyEvent={handleKeyEvent}
      />

      <KeyContext.Provider value={contextValue}>
        {children}
      </KeyContext.Provider>
    </>
  );
};

function useKeys(): KeyContextData {
  const context = useContext(KeyContext);

  if (!context) {
    throw new Error('useKeys must be used within KeyProvider');
  }

  return context;
}

/**
 * Registra um modal que vive fora do KeyProvider (os da página Scout) para que
 * ele também bloqueie o teclado enquanto estiver aberto.
 */
function useModalGate(id: string, isOpen: boolean): void {
  const { setModalOpen } = useKeys();

  useEffect(() => {
    setModalOpen(id, isOpen);
    return () => setModalOpen(id, false);
  }, [id, isOpen, setModalOpen]);
}

export { useKeys, useModalGate, KeyProvider };
